import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRightLine } from '@/components/icons'
import { AppShell, AppButton, CenteredCard } from '@/pages/external/components/shell'
import { Avatar } from '@/pages/external/components'
import {
  buildGrants,
  buildInitialToggles,
  buildPermissionsPayload,
  isRequiredPermission,
  permissionLabel,
  resolveExistingGrants,
  visiblePermissions,
} from '@/pages/external/consentPermissions'
import { submitOAuthConsent, OAuthFlowContext } from '@/services/oauth'
import { User } from '@/types'

interface OAuthConsentProps {
  flowId: string
  context: OAuthFlowContext
  /** Local session user, used to enrich the identity banner when it matches the flow's DUPR ID. */
  sessionUser: User | null
}

type Phase = 'review' | 'approving' | 'denying'

/**
 * OAuth consent screen (authenticated flow). Adapts the legacy ConsentScreen layout — the same
 * required/optional permission toggles — but replaces the entire data layer:
 *  - decisions go to POST /oauth/v1.0/authorize/callback (cookie-bound), never the legacy grant.
 *  - the result is a TOP-LEVEL redirect via window.location.assign — for BOTH approve and deny.
 *  - there is NO postMessage: no token is ever posted to the browser or a partner.
 *
 * The signature element is the identity confirmation banner. On a shared machine the flow may be
 * bound to someone else's account, so the DUPR identity being connected is shown prominently with
 * an explicit "not you?" escape hatch that cancels the authorization (deny) rather than proceeding.
 */
export const OAuthConsent: React.FC<OAuthConsentProps> = ({ flowId, context, sessionUser }) => {
  const { i18n } = useTranslation()
  const { partnerName, connectingUserDuprId, permissions, existingPermissions, existingGrants, missingRequired } =
    context

  // Every descriptor the server sent, in its order — required and optional interleaved as it chose.
  const shownPermissions = visiblePermissions(permissions)
  // A member who connected before and is being asked again: the partner now requires something
  // they have not granted. Worth saying, or the screen looks like a glitch.
  const askingAgain =
    (missingRequired?.length ?? 0) > 0 && !!existingGrants && Object.values(existingGrants).some(Boolean)

  const [toggles, setToggles] = useState<Record<string, boolean>>(() =>
    buildInitialToggles(permissions, resolveExistingGrants(existingGrants, existingPermissions))
  )
  const [phase, setPhase] = useState<Phase>('review')
  const [confirmingSwitch, setConfirmingSwitch] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Enrich the identity banner only when the local session unambiguously matches the flow's user.
  const identityMatchesSession =
    !!sessionUser && !!connectingUserDuprId && sessionUser.referralCode === connectingUserDuprId

  const togglePermission = (key: string) => {
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const redirect = (url: string) => {
    // Top-level navigation off the SPA to the partner redirect_uri (code+state on approve,
    // error+state on deny). Not history.push — this deliberately leaves the DUPR origin.
    window.location.assign(url)
  }

  const handleAuthorize = async () => {
    setError(null)
    setPhase('approving')
    try {
      const grants = buildGrants(permissions, toggles)
      // `grants` is what a current server reads; `permissions` keeps an older one working.
      const { redirectUrl } = await submitOAuthConsent(flowId, true, buildPermissionsPayload(grants), grants)
      redirect(redirectUrl)
    } catch (e) {
      setPhase('review')
      setError(e instanceof Error ? e.message : 'We could not complete the connection. Please try again.')
    }
  }

  const handleDeny = async () => {
    setError(null)
    setPhase('denying')
    try {
      const { redirectUrl } = await submitOAuthConsent(flowId, false)
      redirect(redirectUrl)
    } catch (e) {
      setPhase('review')
      setError(e instanceof Error ? e.message : 'We could not cancel the request. Please try again.')
    }
  }

  const busy = phase !== 'review'

  return (
    <AppShell logoLinksToDashboard={false}>
      <CenteredCard>
        <div className="flex flex-col w-full">
          {/* Eyebrow */}
          <p className="text-xs uppercase tracking-widest text-[color:var(--ink-faint)] text-center mb-3">
            Authorize access
          </p>

          {/* DUPR → Partner indicator */}
          <div className="flex items-center justify-center gap-x-4 py-3 mb-4 border-y border-[color:var(--border)]">
            <img src="/img/dupr_dark.svg" alt="DUPR" className="h-6" style={{ display: 'var(--logo-dark-display)' }} />
            <img
              src="/img/dupr_light.svg"
              alt="DUPR"
              className="h-6"
              style={{ display: 'var(--logo-light-display)' }}
            />
            <ArrowRightLine className="w-5 h-5 text-[color:var(--ink-faint)]" />
            <span className="text-base font-semibold text-[color:var(--ink)]">{partnerName}</span>
          </div>

          {/* Identity confirmation banner — the security anchor of this screen. */}
          <div
            className="rounded-[var(--radius-card)] border p-4 mb-5"
            style={{
              borderColor: 'var(--accent)',
              background: 'color-mix(in srgb, var(--accent) 7%, transparent)',
            }}
          >
            <div className="flex items-center gap-x-3">
              {identityMatchesSession ? (
                <Avatar src={sessionUser?.imageUrl} alt={sessionUser?.fullName} />
              ) : (
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}
                  aria-hidden
                >
                  <svg
                    className="w-6 h-6 text-[color:var(--accent)]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs text-[color:var(--ink-soft)]">You're connecting as</p>
                {identityMatchesSession && (
                  <p className="text-sm font-semibold text-[color:var(--ink)] truncate">{sessionUser?.fullName}</p>
                )}
                <p className="text-base font-bold text-[color:var(--ink)] truncate">
                  DUPR {connectingUserDuprId ?? '—'}
                </p>
              </div>
            </div>

            {/* "Not you?" escape hatch — cancels rather than proceeding under the wrong identity. */}
            <div className="mt-3 pt-3 border-t border-[color:var(--border)]">
              {!confirmingSwitch ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setConfirmingSwitch(true)}
                  className="text-xs font-medium text-[color:var(--accent)] hover:underline disabled:opacity-50"
                >
                  Not you? Switch account
                </button>
              ) : (
                <div className="flex flex-col gap-y-2">
                  <p className="text-xs text-[color:var(--ink-soft)]">
                    This cancels the request and returns you to {partnerName}, where you can sign in with the right DUPR
                    account.
                  </p>
                  <div className="flex items-center gap-x-3">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={handleDeny}
                      className="text-xs font-semibold text-[color:var(--negative)] hover:underline disabled:opacity-50"
                    >
                      {phase === 'denying' ? 'Cancelling…' : 'Yes, this isn’t me'}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setConfirmingSwitch(false)}
                      className="text-xs font-medium text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] disabled:opacity-50"
                    >
                      Stay
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Explanatory text */}
          <p className="text-xs text-[color:var(--ink-soft)] text-center leading-relaxed mb-4">
            <span className="font-medium text-[color:var(--ink)]">{partnerName}</span> is requesting access to the
            following DUPR data. You can change or revoke this from your DUPR profile settings at any time.
          </p>

          {askingAgain && (
            <p className="text-xs text-[color:var(--ink-soft)] text-center leading-relaxed -mt-2 mb-4">
              {partnerName} now asks for more than you shared before, so we need your OK again.
            </p>
          )}

          {/* Permission rows — one per server descriptor, in the server's order. Required rows are
              locked on; optional ones toggle. */}
          <div className="flex flex-col gap-y-2">
            {shownPermissions.map((perm) => {
              const label = permissionLabel(perm, i18n)
              if (isRequiredPermission(perm)) {
                return (
                  <div
                    key={perm.key}
                    className="flex items-center px-3 py-2.5 rounded-[var(--radius-input)] border border-[color:var(--border)]"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="block text-sm text-[color:var(--ink-soft)]">{label}</span>
                      {perm.description && (
                        <span className="block text-xs text-[color:var(--ink-faint)]">{perm.description}</span>
                      )}
                    </div>
                    <span className="text-[10px] font-semibold text-[color:var(--ink-faint)] uppercase mx-3">
                      Required
                    </span>
                    <div
                      className="w-9 h-5 shrink-0 rounded-full bg-[color:var(--accent)] opacity-60 relative cursor-default"
                      aria-hidden
                    >
                      <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-white shadow" />
                    </div>
                  </div>
                )
              }

              const on = !!toggles[perm.key]
              return (
                <button
                  key={perm.key}
                  type="button"
                  disabled={busy}
                  onClick={() => togglePermission(perm.key)}
                  aria-pressed={on}
                  className="flex items-center px-3 py-2.5 rounded-[var(--radius-input)] border border-[color:var(--border)] hover:border-[color:var(--border-strong)] transition-colors disabled:opacity-50 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <span className="block text-sm text-[color:var(--ink)]">{label}</span>
                    {perm.description && (
                      <span className="block text-xs text-[color:var(--ink-soft)]">{perm.description}</span>
                    )}
                  </div>
                  <span className="text-[10px] font-semibold text-[color:var(--ink-faint)] uppercase mx-3">
                    Optional
                  </span>
                  <div
                    className={`w-9 h-5 shrink-0 rounded-full relative transition-colors ${on ? 'bg-[color:var(--accent)]' : 'bg-gray-300'}`}
                    aria-hidden
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'right-0.5' : 'left-0.5'}`}
                    />
                  </div>
                </button>
              )
            })}
          </div>

          {error && (
            <p
              className="mt-4 text-sm text-[color:var(--negative)] bg-[color:var(--negative)]/8 border border-[color:var(--negative)]/20 rounded-[var(--radius-input)] px-3 py-2"
              role="alert"
            >
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex flex-col items-stretch gap-y-2 mt-6">
            <AppButton fullWidth onClick={handleAuthorize} loading={phase === 'approving'} disabled={busy}>
              {phase === 'approving' ? 'Authorizing…' : 'Authorize'}
            </AppButton>
            <AppButton variant="ghost" fullWidth onClick={handleDeny} disabled={busy}>
              {phase === 'denying' ? 'Cancelling…' : 'Deny access'}
            </AppButton>
          </div>
        </div>
      </CenteredCard>
    </AppShell>
  )
}

export default OAuthConsent
